// v5
// Path: frontend/src/components/QuickBooksSync/PipelineConfigModal.jsx
// Inputs: token, config, accounts, dbProfiles, initialDefaults, onClose, onSaved
// Outputs: React JSX modal for creating or editing sync pipeline configurations
// Summary: Pipeline configuration modal with live instance credential manager, Network.Interfaces connection selector, dynamic QB destination entity introspection, connection test diagnostics, live table selector, and locked group hierarchy.

import React, { useState, useEffect } from 'react';

export const PipelineConfigModal = ({ token, config, accounts = [], dbProfiles = [], initialDefaults = {}, onClose, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [availableDbs, setAvailableDbs] = useState([]);
    const [loadingDbs, setLoadingDbs] = useState(false);
    const [dbFetchError, setDbFetchError] = useState(null);
    const [customDbMode, setCustomDbMode] = useState(false);
    const [entityDefaults, setEntityDefaults] = useState([]);
    const [customTableOverride, setCustomTableOverride] = useState(false);

    // Network interface state from Network.Interfaces
    const [availableInterfaces, setAvailableInterfaces] = useState([]);
    const [loadingInterfaces, setLoadingInterfaces] = useState(false);
    const [selectedInterfaceIp, setSelectedInterfaceIp] = useState('');
    const [customIpMode, setCustomIpMode] = useState(false);
    const [customIp, setCustomIp] = useState('');

    // Live table listing for selected database
    const [availableTables, setAvailableTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);

    // Dynamic target QB entities queried from the connected QB account
    const [availableQBEntities, setAvailableQBEntities] = useState([]);
    const [loadingQBEntities, setLoadingQBEntities] = useState(false);

    // SQL Instance Credential & Diagnostics state
    const [showCredSection, setShowCredSection] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [credState, setCredState] = useState({
        username: 'sa',
        password: '',
        ipAddress: '',
        port: 1433,
        instanceId: null,
        instanceName: '',
        serverName: '',
        hasCredential: false
    });
    const [testingConnection, setTestingConnection] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [savingCreds, setSavingCreds] = useState(false);
    const [credSavedNotice, setCredSavedNotice] = useState(false);

    const isGroupAdd = !!initialDefaults.isGroupAdd;

    const [formData, setFormData] = useState({
        configName: '',
        accountId: '',
        sourceConnPrefix: '',
        sourceDatabaseName: '',
        entityType: 'Customers',
        tableName: 'CUSFIL00',
        targetQBEntity: 'Customer',
        syncDirection: 'PBS_TO_QB',
        conflictPolicy: 'SOURCE_WINS',
        isActive: true
    });

    const fallbackQbEntities = [
        { name: 'Customer', label: 'Customers', supported: true, category: 'Entity' },
        { name: 'Vendor', label: 'Vendors', supported: true, category: 'Entity' },
        { name: 'Item', label: 'Products & Services (Items)', supported: true, category: 'Catalog' },
        { name: 'Account', label: 'Chart of Accounts', supported: true, category: 'Financial' },
        { name: 'Invoice', label: 'Invoices', supported: true, category: 'Transaction' },
        { name: 'Bill', label: 'Bills', supported: true, category: 'Transaction' },
        { name: 'Payment', label: 'Receive Payments', supported: true, category: 'Transaction' },
        { name: 'JournalEntry', label: 'Journal Entries', supported: true, category: 'Transaction' },
        { name: 'PurchaseOrder', label: 'Purchase Orders', supported: true, category: 'Transaction' }
    ];

    // Extract last 2 digits of database name for Passport company table suffix
    const getCompanySuffix = (db) => {
        if (!db) return '00';
        const match = db.trim().match(/(\d{2})$/);
        return match ? match[1] : '00';
    };

    // Load dynamic destination entities when target QB account changes
    useEffect(() => {
        if (!formData.accountId) {
            setAvailableQBEntities([]);
            return;
        }
        const fetchEntities = async () => {
            setLoadingQBEntities(true);
            try {
                const res = await fetch(`/api/quickbooks/accounts/${formData.accountId}/entities`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.entities && Array.isArray(data.entities)) {
                        setAvailableQBEntities(data.entities);
                    }
                }
            } catch (err) {
                console.warn('[PipelineConfigModal] Error loading available QB entities:', err);
            } finally {
                setLoadingQBEntities(false);
            }
        };
        fetchEntities();
    }, [formData.accountId, token]);

    // Load entity defaults from database Sync.EntityTableDefaults
    useEffect(() => {
        const fetchDefaults = async () => {
            try {
                const res = await fetch('/api/quickbooks/settings/entity-defaults', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setEntityDefaults(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                console.warn('[PipelineConfigModal] Error loading entity defaults:', e);
            }
        };
        fetchDefaults();
    }, [token]);

    // Fetch instance credentials from Vault.Credentials
    const fetchCredentials = async (prefix) => {
        if (!prefix) return;
        try {
            const res = await fetch(`/api/quickbooks/instance-credential?connPrefix=${encodeURIComponent(prefix)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCredState({
                    username: data.username || 'sa',
                    password: data.password || '',
                    ipAddress: data.ipAddress || '',
                    port: data.port || 1433,
                    instanceId: data.instanceId || null,
                    instanceName: data.instanceName || '',
                    serverName: data.serverName || '',
                    hasCredential: !!data.hasCredential
                });
            }
        } catch (err) {
            console.warn('[PipelineConfigModal] Error fetching instance credential:', err);
        }
    };

    // Fetch available interfaces from Network.Interfaces
    const fetchInterfaces = async (prefix) => {
        if (!prefix) return;
        setLoadingInterfaces(true);
        try {
            const res = await fetch(`/api/quickbooks/instance-interfaces?connPrefix=${encodeURIComponent(prefix)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success && Array.isArray(data.interfaces)) {
                setAvailableInterfaces(data.interfaces);
                // Extract IP from prefix
                const cb = prefix.indexOf(']');
                if (cb !== -1) {
                    const parts = prefix.substring(1, cb).split('|');
                    if (parts.length === 5) {
                        setSelectedInterfaceIp(parts[1]);
                    }
                }
            } else {
                setAvailableInterfaces([]);
            }
        } catch (err) {
            console.warn('[PipelineConfigModal] Error fetching interfaces:', err);
            setAvailableInterfaces([]);
        } finally {
            setLoadingInterfaces(false);
        }
    };

    // Fetch databases for database profile
    const fetchDatabasesForProfile = async (prefix) => {
        if (!prefix) {
            setAvailableDbs([]);
            setDbFetchError(null);
            return;
        }
        setLoadingDbs(true);
        setDbFetchError(null);
        try {
            const res = await fetch(`/api/quickbooks/databases?connPrefix=${encodeURIComponent(prefix)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success && Array.isArray(data.databases)) {
                setAvailableDbs(data.databases);
                setDbFetchError(null);
            } else if (Array.isArray(data)) {
                setAvailableDbs(data.map(d => d.DatabaseName || d));
                setDbFetchError(null);
            } else if (data.error) {
                setAvailableDbs([]);
                setDbFetchError(data.message || data.error || 'Failed to connect to instance');
                setShowCredSection(true); // Automatically expand credentials so user can fix it!
            } else {
                setAvailableDbs([]);
            }
        } catch (err) {
            console.error('[PipelineConfigModal] DB fetch error:', err);
            setAvailableDbs([]);
            setDbFetchError(err.message || 'Network error fetching databases');
            setShowCredSection(true);
        } finally {
            setLoadingDbs(false);
        }
    };

    // Fetch tables for selected database
    const fetchTablesForDatabase = async (prefix, dbName) => {
        if (!prefix || !dbName) {
            setAvailableTables([]);
            return;
        }
        setLoadingTables(true);
        try {
            const res = await fetch(`/api/quickbooks/database-tables?connPrefix=${encodeURIComponent(prefix)}&databaseName=${encodeURIComponent(dbName)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success && Array.isArray(data.tables)) {
                setAvailableTables(data.tables);
            } else {
                setAvailableTables([]);
            }
        } catch (err) {
            console.warn('[PipelineConfigModal] Error fetching tables:', err);
            setAvailableTables([]);
        } finally {
            setLoadingTables(false);
        }
    };

    // Calculate computed Passport table name based on RootTable + suffix
    const computeTableName = (entityType, dbName) => {
        const suffix = getCompanySuffix(dbName);
        const def = entityDefaults.find(d => d.EntityType.toLowerCase() === entityType.toLowerCase());
        const root = def ? def.DefaultRootTable : (
            entityType === 'Customers' ? 'CUSFIL' :
            entityType === 'Items' ? 'ITMFIL' :
            entityType === 'Accounts' ? 'CHARTF' :
            entityType === 'Vendors' ? 'VENFIL' :
            entityType === 'Orders' ? 'OEHDR' :
            entityType.toUpperCase()
        );
        return `${root}${suffix}`;
    };

    useEffect(() => {
        if (config) {
            const prefix = config.SourceConnPrefix || '';
            const db = config.SourceDatabaseName || '';
            setFormData({
                configName: config.ConfigName || '',
                accountId: config.AccountID ? String(config.AccountID) : '',
                sourceConnPrefix: prefix,
                sourceDatabaseName: db,
                entityType: config.EntityType || 'Customers',
                tableName: config.TableName || computeTableName(config.EntityType || 'Customers', db),
                targetQBEntity: config.TargetQBEntity || 'Customer',
                syncDirection: config.SyncDirection || 'PBS_TO_QB',
                conflictPolicy: config.ConflictPolicy || 'SOURCE_WINS',
                isActive: !!config.IsActive
            });
            if (prefix) {
                fetchDatabasesForProfile(prefix);
                fetchCredentials(prefix);
                fetchInterfaces(prefix);
                if (db) fetchTablesForDatabase(prefix, db);
            }
        } else {
            const prefix = initialDefaults.sourceConnPrefix || (dbProfiles.length === 1 ? dbProfiles[0].connPrefix : '');
            const db = initialDefaults.sourceDatabaseName || (isGroupAdd ? '' : 'pbsdata00');
            const acc = initialDefaults.accountId ? String(initialDefaults.accountId) : (accounts.length === 1 ? String(accounts[0].AccountID) : '');
            const entity = initialDefaults.entityType || 'Customers';
            const calculatedTable = computeTableName(entity, db);
            const def = entityDefaults.find(d => d.EntityType.toLowerCase() === entity.toLowerCase());

            setFormData({
                configName: '',
                accountId: acc,
                sourceConnPrefix: prefix,
                sourceDatabaseName: db,
                entityType: entity,
                tableName: calculatedTable,
                targetQBEntity: def ? def.DefaultQBEntity : 'Customer',
                syncDirection: 'PBS_TO_QB',
                conflictPolicy: 'SOURCE_WINS',
                isActive: true
            });
            if (prefix) {
                fetchDatabasesForProfile(prefix);
                fetchCredentials(prefix);
                fetchInterfaces(prefix);
                if (db) fetchTablesForDatabase(prefix, db);
            }
        }
    }, [config, initialDefaults, entityDefaults]);

    const handleProfileChange = (prefix) => {
        setFormData(prev => ({
            ...prev,
            sourceConnPrefix: prefix,
            sourceDatabaseName: ''
        }));
        setTestResult(null);
        setCustomIpMode(false);
        fetchCredentials(prefix);
        fetchInterfaces(prefix);
        fetchDatabasesForProfile(prefix);
    };

    const handleInterfaceChange = (ip) => {
        if (ip === '__CUSTOM__') {
            setCustomIpMode(true);
        } else {
            setCustomIpMode(false);
            setSelectedInterfaceIp(ip);
            const prefix = formData.sourceConnPrefix;
            if (prefix && prefix.startsWith('[')) {
                const cb = prefix.indexOf(']');
                if (cb !== -1) {
                    const parts = prefix.substring(1, cb).split('|');
                    if (parts.length === 5) {
                        parts[1] = ip;
                        const newPrefix = `[${parts.join('|')}]`;
                        setFormData(prev => ({ ...prev, sourceConnPrefix: newPrefix }));
                        fetchDatabasesForProfile(newPrefix);
                    }
                }
            }
        }
    };

    const handleDatabaseChange = (dbName) => {
        setFormData(prev => {
            const newTable = customTableOverride ? prev.tableName : computeTableName(prev.entityType, dbName);
            return {
                ...prev,
                sourceDatabaseName: dbName,
                tableName: newTable
            };
        });
        fetchTablesForDatabase(formData.sourceConnPrefix, dbName);
    };

    const handleEntityTypeChange = (type) => {
        const def = entityDefaults.find(d => d.EntityType.toLowerCase() === type.toLowerCase());
        const calculatedTable = computeTableName(type, formData.sourceDatabaseName);
        setFormData(prev => ({
            ...prev,
            entityType: type,
            tableName: customTableOverride ? prev.tableName : calculatedTable,
            targetQBEntity: def ? def.DefaultQBEntity : prev.targetQBEntity
        }));
    };

    // Test connection with credentials in credState
    const handleTestConnection = async () => {
        setTestingConnection(true);
        setTestResult(null);
        try {
            const res = await fetch('/api/quickbooks/test-instance-connection', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    connPrefix: formData.sourceConnPrefix,
                    username: credState.username,
                    password: credState.password,
                    ipAddress: credState.ipAddress,
                    port: credState.port
                })
            });
            const data = await res.json();
            setTestResult(data);
            if (data.success && Array.isArray(data.databases)) {
                setAvailableDbs(data.databases);
                setDbFetchError(null);
            }
        } catch (err) {
            setTestResult({ success: false, error: err.message });
        } finally {
            setTestingConnection(false);
        }
    };

    // Save credentials to Vault.Credentials
    const handleSaveCredentials = async () => {
        setSavingCreds(true);
        setCredSavedNotice(false);
        try {
            const res = await fetch('/api/quickbooks/instance-credential', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    instanceId: credState.instanceId,
                    connPrefix: formData.sourceConnPrefix,
                    username: credState.username,
                    password: credState.password
                })
            });
            const data = await res.json();
            if (data.success) {
                setCredSavedNotice(true);
                setTimeout(() => setCredSavedNotice(false), 3500);
                // Immediately refresh databases with newly saved credentials
                fetchDatabasesForProfile(formData.sourceConnPrefix);
            } else {
                alert(`Error saving credentials: ${data.error}`);
            }
        } catch (err) {
            alert(`Error saving credentials: ${err.message}`);
        } finally {
            setSavingCreds(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.sourceConnPrefix) {
            alert('Please select a saved Database Profile.');
            return;
        }
        if (!formData.sourceDatabaseName) {
            alert('Please select or enter a Source Database.');
            return;
        }
        if (!formData.accountId) {
            alert('Please select a Target QuickBooks Account.');
            return;
        }

        setLoading(true);
        try {
            const label = formData.configName.trim() || `PBS ${formData.entityType} (${formData.sourceDatabaseName}) -> QB`;
            const payload = {
                ...formData,
                configName: label,
                configId: config ? config.ConfigID : undefined
            };

            const res = await fetch('/api/quickbooks/configs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save configuration');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            alert(`Save error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const matchedProfile = dbProfiles.find(p => p.connPrefix === formData.sourceConnPrefix);
    const matchedAccount = accounts.find(a => String(a.AccountID) === String(formData.accountId));

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-slate-850 border border-slate-700 rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-4 my-8">
                {/* Header */}
                <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-400">table_chart</span>
                        {config ? 'Edit Table Sync Pipeline' : (isGroupAdd ? 'Add Table to Pipeline Group' : 'New Table Sync Pipeline')}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Group Context Badge (when adding inside an existing group) */}
                {isGroupAdd && (
                    <div className="p-3 bg-blue-950/60 border border-blue-800/80 rounded-xl text-xs space-y-1">
                        <div className="text-blue-300 font-bold uppercase tracking-wider flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[15px]">hub</span>
                            Target Pipeline Connection Group (Locked)
                        </div>
                        <div className="flex items-center gap-2 text-slate-200 font-mono text-[11px] flex-wrap">
                            <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700">
                                {matchedProfile ? matchedProfile.displayName : formData.sourceConnPrefix}
                            </span>
                            <span>&bull;</span>
                            <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-emerald-300 font-bold">
                                {formData.sourceDatabaseName}
                            </span>
                            <span>&bull;</span>
                            <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-purple-300 font-semibold">
                                {matchedAccount ? matchedAccount.AccountName : `QB Account #${formData.accountId}`}
                            </span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-4">
                    {/* Config Label / Name */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                            Pipeline Name / Description
                        </label>
                        <input
                            type="text"
                            placeholder={formData.sourceDatabaseName ? `PBS ${formData.entityType} (${formData.sourceDatabaseName}) -> QB` : 'e.g. PBS Customers -> QBO Sandbox'}
                            value={formData.configName}
                            onChange={(e) => setFormData({ ...formData, configName: e.target.value })}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    {/* Target QuickBooks Account */}
                    {!isGroupAdd && (
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Target QuickBooks Account *
                            </label>
                            <select
                                value={formData.accountId}
                                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                                required
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="">-- Select Connected QB Account --</option>
                                {accounts.map(acc => (
                                    <option key={acc.AccountID} value={acc.AccountID}>
                                        {acc.AccountName} ({acc.Environment === 'sandbox' ? 'Sandbox' : 'Production'}) {acc.IsConnected ? '🟢 Active' : '🔴 Inactive'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Database Profile */}
                    {!isGroupAdd && (
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Database Profile (Remote SQL Instance) *
                            </label>
                            <select
                                value={formData.sourceConnPrefix}
                                onChange={(e) => handleProfileChange(e.target.value)}
                                required
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="">-- Select Saved Database Profile --</option>
                                {dbProfiles.map(prof => (
                                    <option key={prof.connPrefix} value={prof.connPrefix}>
                                        {prof.displayName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Network Interface Selection (Network.Interfaces) */}
                    {!isGroupAdd && formData.sourceConnPrefix && (
                        <div className="p-3 bg-slate-900 border border-slate-750 rounded-xl space-y-1.5">
                            <div className="flex justify-between items-center">
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-emerald-400 text-[16px]">lan</span>
                                    <span>Network Interface / Connection IP (Network.Interfaces) *</span>
                                </label>
                                <span className="text-[11px] text-slate-400">
                                    {loadingInterfaces ? 'Loading...' : `${availableInterfaces.length} registered interfaces`}
                                </span>
                            </div>

                            {customIpMode ? (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="e.g. 10.9.8.1 or localhost"
                                        value={customIp}
                                        onChange={(e) => setCustomIp(e.target.value)}
                                        onBlur={() => {
                                            if (customIp.trim()) handleInterfaceChange(customIp.trim());
                                        }}
                                        className="flex-1 bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white font-mono"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setCustomIpMode(false)}
                                        className="px-2.5 py-1 bg-slate-800 hover:bg-slate-750 text-slate-300 rounded text-xs"
                                    >
                                        Use Interface List
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    <select
                                        value={selectedInterfaceIp}
                                        onChange={(e) => handleInterfaceChange(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-sky-300 font-mono font-bold focus:outline-none focus:border-blue-500"
                                    >
                                        {availableInterfaces.map(iface => (
                                            <option key={iface.InterfaceID} value={iface.IPAddress}>
                                                {iface.IPAddress} &mdash; [{iface.IPType || 'LAN'}] {iface.VpnRole ? `(${iface.VpnRole})` : ''} {iface.IsOnline ? '🟢 Online' : '⚪ Offline'} {iface.Notes ? ` - ${iface.Notes}` : ''}
                                            </option>
                                        ))}
                                        <option value="__CUSTOM__">➕ Enter Custom IP / Hostname...</option>
                                    </select>
                                    <p className="text-[10px] text-slate-400">
                                        Select the exact network interface IP (LAN, VPN, localhost) to connect through.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Credential & Connection Diagnostics Card */}
                    {formData.sourceConnPrefix && (
                        <div className="bg-slate-900/90 border border-slate-750 rounded-xl p-3 space-y-2 text-xs">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 font-semibold text-slate-200">
                                    <span className="material-symbols-outlined text-[16px] text-amber-400">key</span>
                                    <span>SQL Instance Credentials & Connectivity</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowCredSection(!showCredSection)}
                                    className="text-sky-400 hover:text-sky-300 text-[11px] underline"
                                >
                                    {showCredSection ? 'Hide Details' : 'Inspect / Edit Credentials'}
                                </button>
                            </div>

                            {/* Status Indicator */}
                            {loadingDbs ? (
                                <div className="flex items-center gap-1.5 text-slate-400">
                                    <div className="w-3 h-3 border-2 border-sky-400 border-t-transparent rounded-full animate-spin"></div>
                                    <span>Querying SQL Server databases...</span>
                                </div>
                            ) : dbFetchError ? (
                                <div className="p-2 bg-rose-950/60 border border-rose-800 rounded-lg text-rose-200 space-y-1">
                                    <div className="font-bold flex items-center gap-1">
                                        <span>⚠️ Login / Connection Issue:</span>
                                    </div>
                                    <p className="font-mono text-[11px] break-all">{dbFetchError}</p>
                                    <p className="text-[11px] text-slate-300">
                                        Update the login password below and click <strong>"Save to Vault"</strong>.
                                    </p>
                                </div>
                            ) : availableDbs.length > 0 ? (
                                <div className="flex items-center gap-1.5 text-emerald-400 text-[11px]">
                                    <span>🟢 Connected to SQL Server (Found {availableDbs.length} online databases)</span>
                                </div>
                            ) : null}

                            {/* Expandable Credential Editor & Test Harness */}
                            {showCredSection && (
                                <div className="pt-2 border-t border-slate-800 space-y-2 animate-in fade-in duration-150">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] text-slate-400 uppercase font-semibold mb-0.5">
                                                Login Username (in Vault.Credentials)
                                            </label>
                                            <input
                                                type="text"
                                                value={credState.username}
                                                onChange={(e) => setCredState({ ...credState, username: e.target.value })}
                                                className="w-full bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white font-mono"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] text-slate-400 uppercase font-semibold mb-0.5">
                                                Login Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={credState.password}
                                                    onChange={(e) => setCredState({ ...credState, password: e.target.value })}
                                                    placeholder="Enter SQL Server password..."
                                                    className="w-full bg-slate-950 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white font-mono pr-7"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-2 top-2 text-slate-400 hover:text-slate-200 text-xs"
                                                >
                                                    {showPassword ? '🙈' : '👁️'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between pt-1">
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                disabled={testingConnection}
                                                onClick={handleTestConnection}
                                                className="px-2.5 py-1 bg-slate-800 hover:bg-slate-750 text-slate-200 border border-slate-650 rounded text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                            >
                                                {testingConnection && <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                                                <span>Test Connection</span>
                                            </button>

                                            <button
                                                type="button"
                                                disabled={savingCreds}
                                                onClick={handleSaveCredentials}
                                                className="px-2.5 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                            >
                                                {savingCreds && <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                                                <span>Save to Vault</span>
                                            </button>
                                        </div>

                                        {credSavedNotice && (
                                            <span className="text-emerald-400 font-semibold text-[11px] animate-pulse">
                                                ✓ Saved to Vault.Credentials!
                                            </span>
                                        )}
                                    </div>

                                    {/* Test Result Message */}
                                    {testResult && (
                                        <div className={`p-2 rounded text-[11px] font-mono ${testResult.success ? 'bg-emerald-950/60 border border-emerald-800 text-emerald-300' : 'bg-rose-950/60 border border-rose-800 text-rose-300'}`}>
                                            {testResult.success ? (
                                                <div>
                                                    <div className="font-bold">✓ {testResult.message}</div>
                                                    <div className="text-[10px] text-emerald-400/80 mt-0.5">
                                                        Databases: {testResult.databases?.join(', ')}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="font-bold">✗ Connection Test Failed:</div>
                                                    <div className="text-[10px] mt-0.5">{testResult.error}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Source Database Name Dropdown */}
                    {!isGroupAdd && (
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                    Source Database Name *
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setCustomDbMode(!customDbMode)}
                                    className="text-[11px] text-blue-400 hover:text-blue-300 underline"
                                >
                                    {customDbMode ? 'Select from list' : 'Type custom DB'}
                                </button>
                            </div>

                            {customDbMode ? (
                                <input
                                    type="text"
                                    placeholder="e.g. pbsdata00"
                                    value={formData.sourceDatabaseName}
                                    onChange={(e) => handleDatabaseChange(e.target.value)}
                                    required
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 font-mono"
                                />
                            ) : (
                                <select
                                    value={formData.sourceDatabaseName}
                                    onChange={(e) => handleDatabaseChange(e.target.value)}
                                    required
                                    disabled={loadingDbs || availableDbs.length === 0}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 font-mono disabled:opacity-50"
                                >
                                    <option value="">
                                        {loadingDbs ? 'Loading live databases...' : (availableDbs.length === 0 ? '-- No databases loaded (check credentials) --' : '-- Select Database --')}
                                    </option>
                                    {availableDbs.map(db => (
                                        <option key={db} value={db}>{db}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {/* Entity Type & Target QuickBooks Entity */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Entity Type
                            </label>
                            <select
                                value={formData.entityType}
                                onChange={(e) => handleEntityTypeChange(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="Customers">Customers</option>
                                <option value="Items">Inventory Items</option>
                                <option value="Accounts">Chart of Accounts</option>
                                <option value="Vendors">Vendors</option>
                                <option value="Orders">Orders / Invoices</option>
                                <option value="Order Lines">Order Lines</option>
                                <option value="Purchase Orders">Purchase Orders</option>
                            </select>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                    Target QB Destination Entity
                                </label>
                                {loadingQBEntities && (
                                    <span className="text-[10px] text-sky-400 flex items-center gap-1">
                                        <span className="w-2.5 h-2.5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin"></span>
                                        Querying QBO...
                                    </span>
                                )}
                            </div>
                            <select
                                value={formData.targetQBEntity}
                                onChange={(e) => setFormData({ ...formData, targetQBEntity: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-sky-400 font-semibold focus:outline-none focus:border-blue-500"
                            >
                                {(availableQBEntities.length > 0 ? availableQBEntities : fallbackQbEntities).map(ent => (
                                    <option 
                                        key={ent.name} 
                                        value={ent.name}
                                        disabled={ent.supported === false}
                                    >
                                        {ent.label || ent.name} {ent.category ? `[${ent.category}]` : ''} {ent.supported === false ? '⚠️ (Disabled in QBO)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Passport Source Table Name (with Live Table Dropdown + Auto-Computed Suffix) */}
                    <div className="p-3 bg-slate-900/60 border border-slate-750 rounded-xl space-y-2">
                        <div className="flex justify-between items-center">
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                Passport Source Table Name
                            </label>
                            <button
                                type="button"
                                onClick={() => setCustomTableOverride(!customTableOverride)}
                                className="text-[11px] text-blue-400 hover:text-blue-300 underline"
                            >
                                {customTableOverride ? 'Auto-Calculate from Suffix' : 'Manual Override'}
                            </button>
                        </div>

                        {customTableOverride ? (
                            <input
                                type="text"
                                value={formData.tableName}
                                onChange={(e) => setFormData({ ...formData, tableName: e.target.value })}
                                placeholder="e.g. CUSFIL00"
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500"
                            />
                        ) : availableTables.length > 0 ? (
                            <div className="space-y-1">
                                <select
                                    value={formData.tableName}
                                    onChange={(e) => setFormData({ ...formData, tableName: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-sky-300 font-mono font-bold focus:outline-none focus:border-blue-500"
                                >
                                    {availableTables.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400">
                                    Discovered <span className="text-emerald-400 font-semibold">{availableTables.length} live tables</span> in {formData.sourceDatabaseName || 'database'}.
                                </p>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    readOnly
                                    value={formData.tableName}
                                    className="w-full bg-slate-950/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-300 font-mono"
                                />
                                <span className="text-xs bg-slate-800 text-slate-400 px-2 py-2 rounded-lg border border-slate-700 whitespace-nowrap">
                                    Rule: {formData.sourceDatabaseName ? `Root + '${getCompanySuffix(formData.sourceDatabaseName)}'` : 'Auto'}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Sync Direction & Conflict Policy */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Sync Direction
                            </label>
                            <select
                                value={formData.syncDirection}
                                onChange={(e) => setFormData({ ...formData, syncDirection: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="PBS_TO_QB">PBS &rarr; QuickBooks (Source of Truth)</option>
                                <option value="QB_TO_PBS">QuickBooks &rarr; PBS</option>
                                <option value="BIDIRECTIONAL">Bidirectional Sync</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Conflict Policy
                            </label>
                            <select
                                value={formData.conflictPolicy}
                                onChange={(e) => setFormData({ ...formData, conflictPolicy: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="SOURCE_WINS">Source Wins (PBS Overwrites)</option>
                                <option value="TARGET_WINS">Target Wins (QB Overwrites)</option>
                                <option value="NEWEST_WINS">Newest Timestamp Wins</option>
                                <option value="MANUAL">Flag as Conflict for Review</option>
                            </select>
                        </div>
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/25 disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                            <span>{config ? 'Save Pipeline Changes' : (isGroupAdd ? 'Add Table to Group' : 'Create Pipeline')}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PipelineConfigModal;
